import { useState, useEffect, useCallback, useRef } from 'react';
import { EFFECTS } from '../constants/effects';
import { FISH_LIBRARY } from '../constants/fishLibrary';

// --- Module-level constants ---
// Defined outside the hook so they are created once for the lifetime
// of the app, never re-created on re-render.

const LIFE_MODIFIERS = { 3: 20, 2: 0, 1: -20 };

const STAR_MAPPING = {
  common: 1, uncommon: 2, rare: 3, special: 4, legendary: 5, mythical: 5,
};

const RARITY_MULTIPLIERS = {
  common: 1, uncommon: 1.5, rare: 2.5, special: 4, legendary: 10, mythical: 25,
};

const EFFECT_MULTIPLIERS = {
  default: 1, shiny: 1.5, neon: 2, ethereal: 3, prismatic: 5, golden: 10,
};

/**
 * getRarityWeights
 *
 * Pure function — calculates weighted rarity chances from a luck score.
 * Defined at module level since it has no dependency on hook state.
 *
 * @param {number} luckScore - Derived from level, streak, and lives remaining.
 */
const getRarityWeights = (luckScore) => ({
  common:    Math.max(10, 100 - luckScore),
  uncommon:  Math.max(20, 50 + luckScore / 2),
  rare:      Math.max(5,  luckScore / 2),
  special:   Math.max(0,  (luckScore - 30) / 3),
  legendary: Math.max(0,  (luckScore - 60) / 5),
  mythical:  Math.max(0,  (luckScore - 90) / 10),
});

/**
 * rollRarity
 *
 * Pure function — selects a rarity tier via weighted random roll.
 * Extracted from generateCatch to keep each step independently readable.
 *
 * @param {object} weights - Output of getRarityWeights.
 */
const rollRarity = (weights) => {
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  let roll = Math.random() * total;
  for (const [rarity, weight] of Object.entries(weights)) {
    if (roll < weight) return rarity;
    roll -= weight;
  }
  return 'common'; // Fallback — should never be reached
};

/**
 * rollEffect
 *
 * Pure function — selects a visual effect based on species effect group.
 *
 * @param {string[]} effectGroup - The species' EFFECTS tier (BASIC, PREMIUM, UNIQUE).
 */
const rollEffect = (effectGroup) => {
  const roll = Math.random();
  if (effectGroup === EFFECTS.PREMIUM) {
    if (roll < 0.01) return 'golden';
    if (roll < 0.05) return 'prismatic';
    if (roll < 0.10) return 'ethereal';
    if (roll < 0.13) return 'neon';
  } else if (effectGroup === EFFECTS.BASIC) {
    if (roll < 0.15) return 'shiny';
  }
  return 'default';
};

// ---------------------------------------------------------------------------

/**
 * useCatchAnimation
 *
 * Owns the multi-step fish reveal animation and catch generation logic.
 * generateCatch is kept here because it produces the currentCatch object
 * that drives the animation — they are tightly coupled by design.
 *
 * @param {object} options
 * @param {object}   options.stats            - Stats object. Streak affects luck.
 * @param {function} options.updateDirectory  - From useDirectory. Called at catch
 *                                             time to record the discovery before
 *                                             the player decides to keep or sell.
 */
export function useCatchAnimation({ level = 0, stats = {}, updateDirectoryRef } = {}) {

  // Animation phase: 0 = initial tile, 1 = shake, 2 = grow, 3 = reveal
  const [catchStep,      setCatchStep]      = useState(0);
  const [currentCatch,   setCurrentCatch]   = useState(null);
  const [hasCaughtToday, setHasCaughtToday] = useState(false);

  // Refs so generateCatch always reads the latest level/streak/updateDirectory
  // without needing to be re-created via useCallback on every render.
  const levelRef           = useRef(level);
  const streakRef          = useRef(stats.currentStreak ?? 0);

  useEffect(() => { levelRef.current          = level;                      }, [level]);
  useEffect(() => { streakRef.current         = stats.currentStreak ?? 0;  }, [stats.currentStreak]);

  // Auto-advances animation from step 0 → 1 after a short delay.
  useEffect(() => {
    if (!currentCatch || catchStep !== 0) return;
    const timer = setTimeout(() => setCatchStep(1), 500);
    return () => clearTimeout(timer);
  }, [currentCatch, catchStep]);

  /**
   * generateCatch
   *
   * Rolls for rarity, species, effect, weight, and sell price.
   * Wrapped in useCallback with an empty dependency array — reads
   * level, streak, and updateDirectory through refs so it never
   * needs to be re-created.
   *
   * @param {number} currentLives - Lives remaining. Affects the luck modifier.
   */
  const generateCatch = useCallback((currentLives) => {
    const lifeModifier = LIFE_MODIFIERS[currentLives] ?? 0;
    const luckScore    = Math.max(0, (levelRef.current * 5) + (streakRef.current * 2) + lifeModifier);

    const selectedRarity = rollRarity(getRarityWeights(luckScore));

    // 1. Select species FIRST
    const possibleFish = FISH_LIBRARY.filter(f => f.rarities.includes(selectedRarity));
    const species      = possibleFish.length > 0
      ? possibleFish[Math.floor(Math.random() * possibleFish.length)]
      : FISH_LIBRARY[0];

    // 2. Roll effect against the ACTUAL selected species' effect group
    const selectedEffect = rollEffect(species.effects);

    const stars            = STAR_MAPPING[selectedRarity] ?? 1;
    const calculatedWeight = (
      species.baseWeight + Math.random() * (stars * species.baseWeight * 0.5)
    ).toFixed(1);

    const levelMultiplier = 1 + levelRef.current * 0.05;
    const finalPrice      = Math.floor(
      (species.basePrice * RARITY_MULTIPLIERS[selectedRarity] * EFFECT_MULTIPLIERS[selectedEffect]) +
      (parseFloat(calculatedWeight) * 0.5 * levelMultiplier)
    );

    const fish = {
      ...species,
      instanceId: Math.random().toString(36).slice(2, 11),
      rarity:     selectedRarity,
      effect:     selectedEffect,
      stars,
      weight:     calculatedWeight,
      sellPrice:  finalPrice,
    };
    // Update the directory immediately at catch time —
    // before the player decides to keep or sell.
    // Reads through ref so generateCatch never needs to be re-created
    // when updateDirectory changes.
    updateDirectoryRef.current?.(fish);

    return fish;
  }, [updateDirectoryRef]);

  /**
   * debugCatch — dev only.
   * Usage: window.debugCatch('kraken', 'mythical', 'golden')
   * Assigned to window in App.js, not here, to keep this hook pure.
   */
  const debugCatch = useCallback((setView) => (
    id     = 'swordfish',
    rarity = 'common',
    effect = 'default',
  ) => {
    const mockFish = {
      id,
      name:      id.charAt(0).toUpperCase() + id.slice(1),
      rarity,
      effect,
      weight:    (Math.random() * 10).toFixed(2),
      stars:     3,
      sellPrice: 100,
    };
    setCurrentCatch(mockFish);
    setCatchStep(3);
    setView('catch');
  }, []);

  return {
    catchStep,
    setCatchStep,
    currentCatch,
    setCurrentCatch,
    hasCaughtToday,
    setHasCaughtToday,
    generateCatch,
    debugCatch,
  };
}